import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useRoomContext,
  useLocalParticipant,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { RoomEvent, DisconnectReason, Track } from 'livekit-client';
import { X, MicOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VideoCallRoomProps {
  token: string;
  wsUrl: string;
  roomName: string;
  onDisconnect: () => void;
  onConnected?: () => void;
}

// Inner component to access room context
function VideoCallContent({ 
  roomName, 
  onDisconnect, 
  onConnected 
}: { 
  roomName: string; 
  onDisconnect: () => void; 
  onConnected?: () => void;
}) {
  const room = useRoomContext();
  const hasCalledDisconnect = useRef(false);
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const [showMicWarning, setShowMicWarning] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);

  // Show mic warning after a delay if mic is muted
  useEffect(() => {
    if (!isMicrophoneEnabled && !warningDismissed) {
      const timer = setTimeout(() => {
        setShowMicWarning(true);
      }, 3000); // Show warning after 3 seconds of being muted
      return () => clearTimeout(timer);
    } else {
      setShowMicWarning(false);
    }
  }, [isMicrophoneEnabled, warningDismissed]);

  const handleEnableMic = useCallback(async () => {
    try {
      await localParticipant?.setMicrophoneEnabled(true);
      setShowMicWarning(false);
    } catch (err) {
      console.error('Failed to enable microphone:', err);
    }
  }, [localParticipant]);

  const handleDismissWarning = useCallback(() => {
    setWarningDismissed(true);
    setShowMicWarning(false);
  }, []);

  useEffect(() => {
    if (!room) return;

    const handleConnected = () => {
      console.log('Connected to LiveKit room:', roomName);
      onConnected?.();
    };

    const handleDisconnected = (reason?: DisconnectReason) => {
      console.log('Disconnected from LiveKit room:', roomName, 'Reason:', reason);
      // Ensure we only call onDisconnect once
      if (!hasCalledDisconnect.current) {
        hasCalledDisconnect.current = true;
        onDisconnect();
      }
    };

    const handleParticipantDisconnected = (participant: any) => {
      console.log('Participant disconnected:', participant.identity);
      // If only one participant left (self), the other hung up
      const remoteParticipants = Array.from(room.remoteParticipants.values());
      if (remoteParticipants.length === 0 && !hasCalledDisconnect.current) {
        console.log('Other participant left, ending call');
        hasCalledDisconnect.current = true;
        onDisconnect();
      }
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    // If already connected, call the handler
    if (room.state === 'connected') {
      handleConnected();
    }

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [room, roomName, onConnected, onDisconnect]);

  const handleLeaveClick = useCallback(() => {
    if (!hasCalledDisconnect.current) {
      hasCalledDisconnect.current = true;
      room?.disconnect();
      onDisconnect();
    }
  }, [room, onDisconnect]);

  return (
    <div className="relative h-full">
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 bg-background/80 hover:bg-background"
        onClick={handleLeaveClick}
      >
        <X className="h-5 w-5" />
      </Button>

      {/* Mic muted warning banner */}
      {showMicWarning && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 animate-bounce-slow">
          <Alert className="bg-amber-500/95 border-amber-600 text-white shadow-lg max-w-md">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 shrink-0">
                <MicOff className="h-5 w-5" />
                <AlertTriangle className="h-4 w-4" />
              </div>
              <AlertDescription className="text-white font-medium flex-1">
                Your microphone is muted. Others can't hear you!
              </AlertDescription>
              <div className="flex gap-2 shrink-0">
                <Button 
                  size="sm" 
                  variant="secondary"
                  className="bg-white text-amber-700 hover:bg-amber-50 font-semibold"
                  onClick={handleEnableMic}
                >
                  Turn On Mic
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-white hover:bg-amber-600"
                  onClick={handleDismissWarning}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Alert>
        </div>
      )}

      {/* Video conference UI */}
      <VideoConference />
      
      {/* Audio renderer for remote participants */}
      <RoomAudioRenderer />
    </div>
  );
}

export function VideoCallRoom({
  token,
  wsUrl,
  roomName,
  onDisconnect,
  onConnected,
}: VideoCallRoomProps) {
  return (
    <div className="fixed inset-0 z-50 bg-background">
      <LiveKitRoom
        serverUrl={wsUrl}
        token={token}
        connect={true}
        data-lk-theme="default"
        style={{ height: '100%' }}
      >
        <VideoCallContent 
          roomName={roomName}
          onDisconnect={onDisconnect}
          onConnected={onConnected}
        />
      </LiveKitRoom>
    </div>
  );
}
